import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Ic, type IcName } from "./icons";
import { tx } from "../../lib/tx";

export type WinId = "mail" | "files" | "viewer" | "share" | "access" | "guide" | "help" | "apps" | "result" | "summary";

export const MODAL_WINS: WinId[] = ["result", "summary"];

export interface WinMeta {
  id: WinId;
  min: boolean;
  max: boolean;
  closing: boolean;
  x: number;
  y: number;
}

/** Default placement, in percent of the window layer. Each app opens at its own offset so a stack stays readable. */
export const GEOM: Record<WinId, { l: number; t: number; w: number; h: number }> = {
  mail: { l: 24, t: 4, w: 74, h: 90 },
  files: { l: 24, t: 6, w: 74, h: 88 },
  viewer: { l: 24, t: 4, w: 72, h: 90 },
  share: { l: 24, t: 3, w: 74, h: 92 },
  access: { l: 24, t: 4, w: 74, h: 90 },
  guide: { l: 28, t: 4, w: 66, h: 88 },
  help: { l: 30, t: 8, w: 60, h: 80 },
  apps: { l: 24, t: 6, w: 72, h: 86 },
  result: { l: 0, t: 0, w: 0, h: 0 },
  summary: { l: 0, t: 0, w: 0, h: 0 },
};

/** On a short screen (a 768p laptop with browser chrome) apps open nearly full size, like Windows does. */
export const GEOM_COMPACT: Record<WinId, { l: number; t: number; w: number; h: number }> = {
  mail: { l: 27, t: 1.5, w: 71, h: 97 },
  files: { l: 27, t: 1.5, w: 71, h: 97 },
  viewer: { l: 27, t: 1.5, w: 71, h: 97 },
  share: { l: 27, t: 1.5, w: 71, h: 97 },
  access: { l: 27, t: 1.5, w: 71, h: 97 },
  guide: { l: 27, t: 1.5, w: 71, h: 97 },
  help: { l: 27, t: 1.5, w: 71, h: 97 },
  apps: { l: 27, t: 1.5, w: 71, h: 97 },
  result: { l: 0, t: 0, w: 0, h: 0 },
  summary: { l: 0, t: 0, w: 0, h: 0 },
};

const COMPACT_QUERY = "(max-height: 900px)";

export function useCompact(): boolean {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(COMPACT_QUERY);
    const on = () => setCompact(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return compact;
}

export const WIN_ICON: Record<WinId, IcName> = {
  mail: "mail",
  files: "folder",
  viewer: "doc",
  share: "share",
  access: "lock",
  guide: "book",
  help: "help",
  apps: "plug",
  result: "info",
  summary: "check",
};

const CLOSE_MS = 180;

/** Window manager state: order is z-order (last on top). Closing keeps the window mounted for the exit animation. */
export function useWindows() {
  const [wins, setWins] = useState<WinMeta[]>([]);
  const timers = useRef(new Map<WinId, number>());
  const allTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach((h) => window.clearTimeout(h));
      t.clear();
      if (allTimer.current !== undefined) window.clearTimeout(allTimer.current);
    };
  }, []);

  const open = useCallback((id: WinId) => {
    const pending = timers.current.get(id);
    if (pending !== undefined) {
      window.clearTimeout(pending);
      timers.current.delete(id);
    }
    setWins((cur) => {
      const ex = cur.find((w) => w.id === id);
      const rest = cur.filter((w) => w.id !== id);
      const next: WinMeta = ex ? { ...ex, min: false, closing: false } : { id, min: false, max: false, closing: false, x: 0, y: 0 };
      return [...rest, next];
    });
  }, []);

  const close = useCallback((id: WinId) => {
    setWins((cur) => cur.map((w) => (w.id === id ? { ...w, closing: true } : w)));
    const prev = timers.current.get(id);
    if (prev !== undefined) window.clearTimeout(prev);
    timers.current.set(
      id,
      window.setTimeout(() => {
        timers.current.delete(id);
        setWins((cur) => cur.filter((w) => !(w.id === id && w.closing)));
      }, CLOSE_MS),
    );
  }, []);

  const closeAll = useCallback(() => {
    setWins((cur) => cur.map((w) => ({ ...w, closing: true })));
    if (allTimer.current !== undefined) window.clearTimeout(allTimer.current);
    allTimer.current = window.setTimeout(() => {
      allTimer.current = undefined;
      setWins((cur) => cur.filter((w) => !w.closing));
    }, CLOSE_MS);
  }, []);

  const focus = useCallback((id: WinId) => {
    setWins((cur) => {
      const i = cur.findIndex((w) => w.id === id);
      if (i < 0 || i === cur.length - 1) return cur;
      return [...cur.slice(0, i), ...cur.slice(i + 1), cur[i]];
    });
  }, []);

  const minimize = useCallback((id: WinId) => {
    setWins((cur) => cur.map((w) => (w.id === id ? { ...w, min: true } : w)));
  }, []);

  const toggleMax = useCallback((id: WinId) => {
    setWins((cur) => cur.map((w) => (w.id === id ? { ...w, max: !w.max, x: 0, y: 0 } : w)));
  }, []);

  const move = useCallback((id: WinId, x: number, y: number) => {
    setWins((cur) => cur.map((w) => (w.id === id ? { ...w, x, y } : w)));
  }, []);

  return { wins, open, close, closeAll, focus, minimize, toggleMax, move };
}

export function activeWinId(wins: WinMeta[]): WinId | null {
  for (let i = wins.length - 1; i >= 0; i -= 1) {
    if (!wins[i].min && !wins[i].closing) return wins[i].id;
  }
  return null;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

export function SisWin({
  meta,
  z,
  active,
  compact,
  title,
  className,
  modal,
  onFocus,
  onClose,
  onMin,
  onMax,
  onMove,
  pulseClose,
  children,
}: {
  meta: WinMeta;
  z: number;
  active: boolean;
  compact: boolean;
  title: string;
  className?: string;
  /** Modal windows are centred over a dim backdrop and have no window controls. */
  modal?: boolean;
  onFocus: () => void;
  onClose: () => void;
  onMin: () => void;
  onMax: () => void;
  onMove: (x: number, y: number) => void;
  pulseClose?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const g = (compact ? GEOM_COMPACT : GEOM)[meta.id];

  useEffect(() => {
    // Give keyboard focus to a freshly opened window so Esc and Tab work inside it.
    ref.current?.focus({ preventScroll: true });
  }, []);

  const style = {
    ...(modal
      ? {}
      : {
          "--l": `${g.l}%`,
          "--t": `${g.t}%`,
          "--w": `${g.w}%`,
          "--h": `${g.h}%`,
          "--dx": `${meta.x}px`,
          "--dy": `${meta.y}px`,
        }),
    zIndex: z,
  } as CSSProperties;

  const onTitleDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (modal || meta.max || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button")) return;
    const el = ref.current;
    const layer = el?.parentElement;
    if (!el || !layer) return;
    const lr = layer.getBoundingClientRect();
    const wr = el.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = meta.x;
    const oy = meta.y;
    const keep = 90;
    const minX = ox - (wr.left - lr.left) - (wr.width - keep);
    const maxX = ox + (lr.right - wr.left) - keep;
    const minY = oy - (wr.top - lr.top);
    const maxY = oy + (lr.bottom - wr.top) - 34;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Capture is a nicety; the window-level listeners below carry the drag either way.
    }
    setDragging(true);
    const move = (ev: PointerEvent) => onMove(clamp(ox + ev.clientX - sx, minX, maxX), clamp(oy + ev.clientY - sy, minY, maxY));
    const up = () => {
      setDragging(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && !modal) {
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      ref={ref}
      className={[
        "sis-win",
        `sis-win--${meta.id}`,
        className ?? "",
        active ? "is-active" : "",
        meta.max ? "is-max" : "",
        meta.min ? "is-min" : "",
        meta.closing ? "is-closing" : "",
        dragging ? "is-dragging" : "",
        modal ? "is-modal" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role="dialog"
      aria-label={title}
      aria-hidden={meta.min || undefined}
      tabIndex={-1}
      onPointerDownCapture={onFocus}
      onKeyDown={onKey}
    >
      <div className="win-titlebar sis-tb" onPointerDown={onTitleDown} onDoubleClick={() => !modal && onMax()}>
        <span className="win-titlebar-name sis-tb-name">
          <Ic n={WIN_ICON[meta.id]} size={14} />
          {title}
        </span>
        {!modal && (
          <span className="win-titlebar-controls sis-tb-ctl">
            <button type="button" className="wt min" aria-label={tx("Minimise")} onClick={onMin}>
              <Ic n="minus" size={14} />
            </button>
            <button type="button" className="wt max" aria-label={meta.max ? tx("Restore") : tx("Maximise")} onClick={onMax}>
              <Ic n={meta.max ? "restore" : "square"} size={14} />
            </button>
            <button type="button" className={`wt close${pulseClose ? " blink" : ""}`} aria-label={tx("Close")} onClick={onClose}>
              <Ic n="close" size={14} />
            </button>
          </span>
        )}
      </div>
      <div className="sis-win-body">{children}</div>
    </div>
  );
}
